import { Input, InputProps } from '@/ui/Input'
import {
    autoUpdate,
    flip,
    FloatingPortal,
    offset,
    Placement,
    useClick,
    useDismiss,
    useFloating,
    useFloatingNodeId,
    useInteractions,
    useTransitionStatus,
} from '@floating-ui/react'
import { useState, useEffect, KeyboardEvent, forwardRef, ForwardedRef, useRef } from 'react'
import styles from './SuggestionInputTable.module.scss'
import clsx from 'clsx'

export type Option = {
    id: string
    label: string
    value: string
}

interface SuggestionInputTableProps extends Omit<InputProps, 'onChange' | 'value'> {
    options: Option[]
    value?: string
    onChange: (value: string) => void
    placement?: Placement
    displayValueInsteadOfLabel?: boolean
    inputClassName?: string
    canTabThrough?: boolean
    onlyFromDropdown?: boolean
    onBlurHandler?: (value: string) => void
    isNoFiltering?: boolean
}

export const SuggestionInputTable = forwardRef(
    (
        {
            options,
            value = '',
            onChange,
            onFocus,
            onBlurHandler,
            className,
            placement = 'bottom',
            displayValueInsteadOfLabel = false,
            inputClassName,
            canTabThrough = false,
            onlyFromDropdown = false,
            isNoFiltering = false,
            ...props
        }: SuggestionInputTableProps,
        ref?: ForwardedRef<HTMLInputElement>
    ) => {
        const rootRef = useRef<HTMLDivElement>(null)
        const [isOpen, setIsOpen] = useState(false)
        const [highlightedIndex, setHighlightedIndex] = useState<number | null>(null)
        const [inputValue, setInputValue] = useState(value || '')
        const mouseHasMoved = useRef(false)

        const nodeId = useFloatingNodeId()

        const { x, y, strategy, context, refs } = useFloating({
            nodeId,
            open: isOpen,
            onOpenChange: setIsOpen,
            whileElementsMounted: (reference, floating, update) => {
                const cleanup = autoUpdate(reference, floating, update)
                const handleScroll = (event: Event) => {
                    if (floating && event.target === floating) return
                    setIsOpen(false)
                }

                const scrollContainer = rootRef.current?.closest('[data-scrollable-table]')
                if (scrollContainer) {
                    scrollContainer.addEventListener('scroll', handleScroll)
                }

                return () => {
                    cleanup()
                    if (scrollContainer) {
                        scrollContainer.removeEventListener('scroll', handleScroll)
                    }
                }
            },
            middleware: [offset({ mainAxis: 5 }), flip()],
            placement,
        })

        const dismiss = useDismiss(context)
        const click = useClick(context)
        const { isMounted, status } = useTransitionStatus(context)
        const { getFloatingProps } = useInteractions([click, dismiss])

        const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
            const newValue = e.target.value
            setInputValue(newValue)
            mouseHasMoved.current = false
            setIsOpen(true)
            setHighlightedIndex(null)

            !onlyFromDropdown && onChange(newValue)
        }

        const handleBlur = () => {
            if (onBlurHandler) {
                if (options.some((opt) => opt.value === inputValue)) {
                    onBlurHandler(inputValue)
                } else {
                    onChange(value)
                }
            }
        }

        useEffect(() => {
            const matched = options.find((opt) => opt.value === value)
            if (displayValueInsteadOfLabel) {
                setInputValue(value || '')
            } else if (matched) {
                setInputValue(matched.label)
            } else {
                setInputValue(value || '')
            }
        }, [value, options, displayValueInsteadOfLabel])

        const filteredOptions = options.filter((option) =>
            isNoFiltering ? true : option.label.toLowerCase().includes(inputValue.toLowerCase())
        )

        const handleSelect = (selected: Option) => {
            if (displayValueInsteadOfLabel) {
                setInputValue(selected.value)
            } else {
                setInputValue(selected.label)
            }
            onChange(selected.value)
            setIsOpen(false)

            // Move focus to the next field after selection (similar to Tab behavior)
            setTimeout(() => {
                const referenceElement = refs.reference?.current as HTMLElement
                const inputElement = referenceElement?.querySelector('input')
                if (inputElement) {
                    const form = inputElement.closest('form')
                    if (form) {
                        const focusableElements = Array.from(
                            form.querySelectorAll(
                                'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
                            )
                        ).filter((el) => {
                            // Filter only focusable elements
                            const element = el as HTMLElement
                            return (
                                !('disabled' in element && element.disabled) &&
                                element.style.display !== 'none' &&
                                element.tabIndex !== -1
                            )
                        }) as HTMLElement[]
                        const currentIndex = focusableElements.findIndex(
                            (el) => el === inputElement || el.contains(inputElement)
                        )
                        if (currentIndex > -1 && currentIndex < focusableElements.length - 1) {
                            focusableElements[currentIndex + 1].focus()
                        }
                    }
                }
            }, 0)
        }

        const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
            if (e.key === 'Escape') {
                setIsOpen(false)
            }
            if (e.key === 'ArrowDown') {
                e.preventDefault()
                setHighlightedIndex((prev) => (prev === null || prev === filteredOptions.length - 1 ? 0 : prev + 1))
            }
            if (e.key === 'ArrowUp') {
                e.preventDefault()
                setHighlightedIndex((prev) => (prev === null || prev === 0 ? filteredOptions.length - 1 : prev - 1))
            }
            if (e.key === 'Enter') {
                e.preventDefault()
                if (highlightedIndex !== null && filteredOptions[highlightedIndex]) {
                    handleSelect(filteredOptions[highlightedIndex])
                    return
                }

                if (filteredOptions.length >= 1 && inputValue.length) {
                    handleSelect(filteredOptions[0])
                    return
                }

                if (filteredOptions.length >= 1 && !inputValue.length) {
                    setIsOpen(false)
                }

                if (!filteredOptions.length && onlyFromDropdown) {
                    setInputValue('')
                    onChange('')
                }

                // Move focus to the next field after selection (similar to Tab behavior)
                setTimeout(() => {
                    const referenceElement = refs.reference?.current as HTMLElement
                    const inputElement = referenceElement?.querySelector('input')
                    if (inputElement) {
                        const form = inputElement.closest('form')
                        if (form) {
                            const focusableElements = Array.from(
                                form.querySelectorAll(
                                    'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
                                )
                            ).filter((el) => {
                                // Filter only focusable elements
                                const element = el as HTMLElement
                                return (
                                    !('disabled' in element && element.disabled) &&
                                    element.style.display !== 'none' &&
                                    element.tabIndex !== -1
                                )
                            }) as HTMLElement[]
                            const currentIndex = focusableElements.findIndex(
                                (el) => el === inputElement || el.contains(inputElement)
                            )
                            if (currentIndex > -1 && currentIndex < focusableElements.length - 1) {
                                focusableElements[currentIndex + 1].focus()
                            }
                        }
                    }
                }, 0)
            }
            if (e.key === 'Tab') {
                if (highlightedIndex !== null && filteredOptions[highlightedIndex]) {
                    handleSelect(filteredOptions[highlightedIndex])
                    return
                }

                if (filteredOptions.length >= 1 && !canTabThrough) {
                    setIsOpen(false)
                }

                if (filteredOptions.length >= 1 && canTabThrough) {
                    setIsOpen(false)
                }

                if (!filteredOptions.length && onlyFromDropdown) {
                    setInputValue('')
                    onChange('')
                    setIsOpen(false)
                }

                // Move focus to the next field after selection (similar to Tab behavior)
                setTimeout(() => {
                    const referenceElement = refs.reference?.current as HTMLElement
                    const inputElement = referenceElement?.querySelector('input')
                    if (inputElement) {
                        const form = inputElement.closest('form')
                        if (form) {
                            const focusableElements = Array.from(
                                form.querySelectorAll(
                                    'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
                                )
                            ).filter((el) => {
                                // Filter only focusable elements
                                const element = el as HTMLElement
                                return (
                                    !('disabled' in element && element.disabled) &&
                                    element.style.display !== 'none' &&
                                    element.tabIndex !== -1
                                )
                            }) as HTMLElement[]
                            const currentIndex = focusableElements.findIndex(
                                (el) => el === inputElement || el.contains(inputElement)
                            )
                            if (currentIndex > -1 && currentIndex < focusableElements.length - 1) {
                                focusableElements[currentIndex + 1].focus()
                            }
                        }
                    }
                }, 0)
            }
        }

        return (
            <div onKeyDown={handleKeyDown} ref={rootRef}>
                <div ref={refs.setReference}>
                    <Input
                        value={inputValue}
                        onChange={handleInputChange} // Allow free text input
                        onFocus={(event) => {
                            mouseHasMoved.current = false
                            setIsOpen(true)
                            onFocus?.(event)
                        }}
                        onBlur={handleBlur}
                        className={inputClassName}
                        ref={ref}
                        {...props}
                    />
                </div>

                <FloatingPortal id={nodeId} root={rootRef.current}>
                    {isMounted && filteredOptions.length > 0 && (
                        <div
                            ref={refs.setFloating}
                            style={{ position: strategy, top: y ?? 0, left: x ?? 0, zIndex: 1000 }}
                            className={className}
                            data-status={status}
                            data-click-outside-exclude
                            {...getFloatingProps()}
                        >
                            <ul
                                className={clsx(styles.dropdownOptions, {
                                    [styles.dropdownOptionsOpen]: isOpen,
                                })}
                                role="listbox"
                            >
                                {filteredOptions.map((option, index) => (
                                    <li
                                        key={option.id}
                                        role="option"
                                        className={clsx(styles.dropdownOption, {
                                            [styles.highlighted]: highlightedIndex === index,
                                        })}
                                        onMouseMove={() => { mouseHasMoved.current = true }}
                                        onMouseDown={() => {
                                            handleSelect(option)
                                        }}
                                        onMouseEnter={() => {
                                            if (mouseHasMoved.current) setHighlightedIndex(index)
                                        }}
                                    >
                                        {option.label}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </FloatingPortal>
            </div>
        )
    }
)
